import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { publicApi } from '../../api/public-client';
import { StateSwitch } from '../../motion';
import { palette, spacing, typography } from '../../style-utils/theme';
import { BillingInterval, IntervalToggle } from '../billing/interval-toggle';
import {
  annualBadgePercent,
  effectiveLabel,
  priceLabel,
} from '../billing/plan-display';
import {
  PlanOptionList,
  PlanOption,
  PlanOptionName,
  PlanOptionPrice,
  HelperText,
  IqBadge,
  SkeletonGroup,
  SkeletonOption,
} from './onboarding.styled';

// One row of the public catalog (GET /api/plans). Since migration 105 a row is
// one PRICE: a tier (desk | desk_iq | custom) at one interval. The last five
// fields are computed server-side from the two real amounts
// (src/billing/price-rules.js) — nothing on this screen derives a saving.
export interface Plan {
  id: number;
  code: string;
  name: string;
  description?: string;
  tier?: string | null;
  price_cents: number;
  currency: string;
  billing_interval: string;
  is_per_unit: boolean;
  contact_sales: boolean;
  iq_enabled: boolean;
  counterpart_plan_id?: number | null;
  effective_monthly_cents?: number | null;
  monthly_premium_percent?: number | null;
  annual_savings_percent?: number | null;
  annual_savings_badge_percent?: number | null;
}

interface PlanPickerProps {
  selectedId: number | null;
  onSelect: (plan: Plan) => void;
  // Plan code to preselect instead of the usual default — set from ?plan= when
  // the visitor arrived from a specific plan's CTA on the marketing site. An
  // unknown code is ignored rather than treated as an error: the catalog is the
  // authority on what exists, and a stale marketing link must never break
  // signup.
  preferCode?: string | null;
  // ?interval= from the same link. The row picked is still the catalog's.
  preferInterval?: BillingInterval | null;
}

// The price wording lives in ../billing/plan-display (pure, no API client) so
// the billing page — and its tests — can use it without this module's axios.
export { priceLabel, effectiveLabel, annualBadgePercent };

// The catalogue has three choices on screen; three option-shaped rows reserve
// the height the list will take, so the Continue button under it does not
// climb the card while the request is in flight.
const SKELETON_ROWS = 3;

// Reads the PUBLIC plan catalog (no auth) so it can render during signup,
// before an account exists. Monthly / Annual chooses which rows are shown;
// switching carries the selection to the same plan at the other interval.
// Preselects the AI (Desk + IQ) plan on first load like the mockup.
export const PlanPicker: React.FC<PlanPickerProps> = ({
  selectedId,
  onSelect,
  preferCode = null,
  preferInterval = null,
}) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [interval, setInterval] = useState<BillingInterval>(
    preferInterval === 'year' ? 'year' : 'month'
  );

  // Fetch the catalog once. Defensive: a misconfigured API base can return the
  // SPA's index.html (a string) instead of JSON — never feed a non-array to .map().
  useEffect(() => {
    let alive = true;
    publicApi
      .get('/plans')
      .then(res => {
        if (alive) setPlans(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const hasAnnual = plans.some(p => p.billing_interval === 'year');
  const visible = useMemo(
    () =>
      plans.filter(
        p => p.contact_sales || !hasAnnual || p.billing_interval === interval
      ),
    [plans, interval, hasAnnual]
  );
  const badge = annualBadgePercent(plans);

  // Preselect once the catalog loads, if nothing is chosen yet. A requested
  // code wins (and brings its own interval); otherwise Desk + IQ at the
  // current interval, like the mockup. Proper deps so no eslint-disable is
  // needed; onSelect (a useState setter) is stable, and the guard stops it
  // re-running once a plan is selected.
  useEffect(() => {
    if (selectedId !== null || plans.length === 0) return;
    const requested = preferCode
      ? plans.find(p => p.code === preferCode)
      : undefined;
    if (requested) {
      if (!requested.contact_sales && requested.billing_interval === 'year') {
        setInterval('year');
      }
      onSelect(requested);

      return;
    }
    const preselect = plans.find(
      p =>
        p.iq_enabled &&
        !p.contact_sales &&
        (!hasAnnual || p.billing_interval === interval)
    );
    if (preselect) onSelect(preselect);
  }, [plans, selectedId, onSelect, preferCode, interval, hasAnnual]);

  // Switching interval carries the choice to the same plan at the other
  // interval (the server names that row as `counterpart_plan_id`).
  const switchInterval = (next: BillingInterval) => {
    setInterval(next);
    const current = plans.find(p => p.id === selectedId);
    if (current && !current.contact_sales && current.billing_interval !== next) {
      const twin = plans.find(p => p.id === current.counterpart_plan_id);
      if (twin) onSelect(twin);
    }
  };

  const state = loading
    ? 'loading'
    : error
      ? 'error'
      : plans.length
        ? 'ready'
        : 'empty';

  return (
    <StateSwitch state={state}>
      {state === 'loading' && (
        <SkeletonGroup label="Loading plans">
          <PlanOptionList aria-hidden="true">
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <SkeletonOption key={i} />
            ))}
          </PlanOptionList>
        </SkeletonGroup>
      )}
      {state === 'error' && (
        <HelperText>Could not load plans. Please try again.</HelperText>
      )}
      {state === 'empty' && (
        <HelperText>No plans are available right now.</HelperText>
      )}
      {state === 'ready' && (
        <>
          {hasAnnual && (
            <ToggleRow>
              <IntervalToggle
                value={interval}
                onChange={switchInterval}
                savingsPercent={badge}
              />
            </ToggleRow>
          )}
          <PlanOptionList>
            {visible.map(plan => {
              const effective = effectiveLabel(plan);

              return (
                <PlanOption
                  key={plan.id}
                  type="button"
                  selected={selectedId === plan.id}
                  onClick={() => onSelect(plan)}
                  aria-pressed={selectedId === plan.id}
                >
                  <PlanOptionName>
                    {plan.name}
                    {plan.iq_enabled && !plan.contact_sales && (
                      <IqBadge>AI</IqBadge>
                    )}
                  </PlanOptionName>
                  <PriceStack>
                    <PlanOptionPrice>{priceLabel(plan)}</PlanOptionPrice>
                    {effective && <Effective>{effective}</Effective>}
                  </PriceStack>
                </PlanOption>
              );
            })}
          </PlanOptionList>
        </>
      )}
    </StateSwitch>
  );
};

const ToggleRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing.sm};
`;

const PriceStack = styled.span`
  margin-left: auto;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  gap: 2px;

  /* The price sits in the stack, so the stack takes the auto margin and the
     price's own must be reset. A plain child selector, NEVER an interpolated
     component (\`\${PlanOptionPrice}\`): CRA ships without @emotion/babel-plugin,
     and a component selector throws at render — it took the whole signup page
     down ("Component selectors can only be used in conjunction with
     @emotion/babel-plugin"), caught by the browser journeys on 2026-09-15. */
  & > * {
    margin-left: 0;
  }
`;

const Effective = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;
